import { useContext, useState, useEffect, forwardRef } from 'react';

import RoundButton from 'approot/shared/round-icon-button/round-icon-button';
import { AccordionToggleProps as Props } from './accordion.constants';
import { AccordionContext, SelectableContext } from './accordion.context';
import {
  AccordionPanel,
  AccordionContent,
  AccordionButtonIcon,
  AccordionButton,
  AccordionLabel,
  AccordionHeading,
} from './accordion.styles';

export const AccordionToggle = forwardRef(
  (
    {
      title,
      children,
      ariaLabel,
      defaultExpanded = false,
      hideWithCSSVisibility = true,
      labelComponent,
      isAccordionShell,
      openOverride,
      onClick,
      headingElementType = 'h2',
    }: Props,
    ref
  ) => {
    const { id, onHeaderKeyDown, onToggle } = useContext(AccordionContext);
    const { index } = useContext(SelectableContext);
    const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);

    useEffect(() => {
      // if openOverride is not set it shouldn't override default
      if (openOverride !== undefined) {
        setIsExpanded(openOverride);
      }
    }, [openOverride]);

    return (
      <AccordionPanel
        data-selector="accordion-panel"
        isAccordionShell={isAccordionShell}
        className={isExpanded ? 'expanded' : 'collapsed'}
      >
        <AccordionHeading as={headingElementType}>
          <AccordionButton
            ref={ref as any}
            data-accordion-id={id}
            className="accordion-trigger"
            id={`${id}-header-${index}`}
            aria-expanded={isExpanded}
            aria-controls={`${id}-content-${index}`}
            aria-label={ariaLabel || undefined}
            onClick={e => {
              setIsExpanded(!isExpanded);
              if (onClick) onClick(e, !isExpanded);
              onToggle(!isExpanded);
            }}
            onKeyDown={e => onHeaderKeyDown && onHeaderKeyDown(e)}
            tabIndex={isAccordionShell ? -1 : 0}
            isAccordionShell={isAccordionShell}
          >
            {labelComponent ? (
              labelComponent
            ) : (
              <AccordionLabel data-selector="content-section-title">
                {title}
              </AccordionLabel>
            )}
            <AccordionButtonIcon isExpanded={isExpanded}>
              <RoundButton
                noBorder
                className="accordion-trigger-button"
                tabIndex={isAccordionShell ? 0 : -1}
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'}`}
                iconType="icon_dropdown"
                iconWidth={20}
                iconHeight={20}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  setIsExpanded(!isExpanded);
                  if (onClick) onClick(e, !isExpanded);
                  onToggle(!isExpanded);
                }}
                data-selector={`${isExpanded ? 'collapse' : 'expand'}`}
              />
            </AccordionButtonIcon>
          </AccordionButton>
        </AccordionHeading>
        <AccordionContent
          id={`${id}-content-${index}`}
          role="region"
          aria-labelledby={`${id}-header-${index}`}
          isExpanded={isExpanded}
          hideWithCSSVisibility={hideWithCSSVisibility}
          tabIndex={-1}
          isAccordionShell={isAccordionShell}
        >
          {children}
        </AccordionContent>
      </AccordionPanel>
    );
  }
);

export default AccordionToggle;
